/**
 * SEC dilutive-offering form classification — shared by the Recent Offerings
 * page and any other surface that shows offering filings (DilutionSignals,
 * scanner views, etc.). Born shared so form labels/colours stay in one place.
 *
 * Backend source of truth: api/src/services/altdata/secEdgarOfferingProvider.ts
 *   OFFERING_FORMS = ['S-1', 'S-3', '424B5', '424B3', '424B2', 'FWP'] (+ /A amendments)
 */

export type FormFamily = 'S-1' | 'S-3' | '424B' | 'FWP' | 'other';

/** Map a raw EDGAR form (incl. /A amendments) to one of our root families. */
export function formFamily(form: string): FormFamily {
  const f = (form || '').toUpperCase();
  if (f.startsWith('424B')) return '424B';
  if (f.startsWith('S-1')) return 'S-1';
  if (f.startsWith('S-3')) return 'S-3';
  if (f.startsWith('FWP')) return 'FWP';
  return 'other';
}

/** Plain-English description of what each filing family signals. */
export function formDescription(form: string): string {
  switch (formFamily(form)) {
    case 'S-1': return 'Registration statement (IPO / resale)';
    case 'S-3': return 'Shelf registration';
    case '424B': return 'Prospectus supplement (shelf takedown)';
    case 'FWP': return 'Free writing prospectus';
    default: return 'SEC dilutive filing';
  }
}

/** Colour a form badge by family — 424B (the actual takedown, most dilutive)
 *  reads hottest; the S-3 shelf (potential) is amber. */
export function formBadgeClass(form: string): string {
  switch (formFamily(form)) {
    case '424B': return 'bg-red-500/15 text-red-400 border border-red-500/25';
    case 'S-1': return 'bg-orange-500/15 text-orange-400 border border-orange-500/25';
    case 'S-3': return 'bg-amber-500/15 text-amber-400 border border-amber-500/25';
    case 'FWP': return 'bg-sky-500/15 text-sky-400 border border-sky-500/25';
    default: return 'bg-slate-500/15 text-fg-muted border border-default';
  }
}
